import { FC, useContext, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query";
import { ZipProjectContext } from "./ZipProjectContext";
import { ResourceStatus, ResourceType } from "@app/resources";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ModalLink } from "@app/shared/Modals";
import { Link, useLocation } from "react-router-dom";
import { EditProject } from "../network-assessment/EditProject";
import { isProjectRunning, onRunProject } from "./utility";
import { ZipJobsPanelContentLink } from "../network-assessment/ZipJobsPanelContentLink";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { BreadcrumbTemplateEnum, breadcrumbTemplates } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import ResourceService from "@app/resources/resourceService";
import ProviderActionsDrawerFlyout from "../network-assessment/provider-actions/ProviderActionsDrawerFlyout";

export const ZipProjectControls: FC = () => {
  const projectContext = useContext(ZipProjectContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const queryClient = useQueryClient();

  const { state, dispatch } = projectContext;
  const [projectOptionsDirty, setProjectOptionsDirty] = useState(false);
  const resourceService = useRef(new ResourceService()).current;
  const location = useLocation();

  const { isArchived, isShared, name, resourceId } = state.project.resource;
  const { isReadOnly } = state.project;
  const isRunning = isProjectRunning(state.project);
  const archiveText = isArchived ? "Unarchive" : "Archive";

  const onSubmitEditProject = () => {
    projectContext.onSubmitEditProject(state.project.resource.resourceId);
    void queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "project" && String(query.queryKey[2]) === String(resourceId),
    });
  }

  const handleArchiveConfirm = async () => {
    if (isRunning) {
      return;
    }

    if (isArchived) {
      await resourceService.updateResourceArchiveStatus(resourceId, ResourceStatus.Active);
    } else {
      await resourceService.updateResourceArchiveStatus(resourceId, ResourceStatus.Archived);
    }
    window.location.href = `${window.customUIBaseUrl}/`;
  }

  return (
    <>
      <FlyoutLink
        content={{
          body: (
            <EditProject
              sidePanelId={""}
              disabled={isReadOnly || state.project.isEditRestricted}
              onChange={(dirty: boolean) => setProjectOptionsDirty(dirty)}
              projectId={resourceId}
              onRunProject={async () => onRunProject(dispatch, projectContext.onRunProject, resourceId)}
              onSubmitSuccess={async () => { onSubmitEditProject() }}
            />
          ),
          title: "Project Options",
        }}
        modalContent={{
          body: "You will lose unsaved changes. Continue with closing the sidebar?",
          cancelButtonText: "Cancel",
          confirmButtonText: "Continue",
          title: "Warning",
        }}
        size="80"
        warnOnClose={projectOptionsDirty}
        testId="zip-project-controls__project-options-link"
        onClose={() => { setProjectOptionsDirty(false) }}
      >
        <span className="project-action-button">
          <QesIcon iconName={QesIcons.PencilV1} description="Edit Project Options" />
          {isShared ? " " : " Edit "}Project Options
        </span>
      </FlyoutLink >

      {(enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING) || enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION))
        && (
        <ProviderActionsDrawerFlyout
          testId="zip-project-controls__provider-actions-flyout-link"
          disabled={isReadOnly}
          classNames="ml10"
          resetBanner={async (hasUnprocessedTerminations, hasUnprocessedTargets, hasUnprocessedExcludes) =>
            projectContext.onUpdateUnprocessedProviders(hasUnprocessedTerminations, hasUnprocessedTargets, hasUnprocessedExcludes)}
        >
          <span className={`project-action-button ${isReadOnly ? " disabled" : ""}`}>
            <QesIcon iconName={QesIcons.Zap} description="Provider Action Management" /> Provider Action Management
          </span>
        </ProviderActionsDrawerFlyout>
      )}

      {!isShared && (
        <>
          <span className="ml10">
            <Link
              to={{
                pathname: `/resources/share`,
                state: {
                  breadcrumbItems: [
                    ...breadcrumbTemplates[BreadcrumbTemplateEnum.NetworkAssessment],
                    {
                      link: location.pathname,
                      text: state.project.resource.name,
                    }
                  ],
                  isSingleResource: true,
                  resourceDescription: "Project",
                  resourceId: state.project.resource.resourceId,
                  resourceTypes: [ResourceType.Project],
                },
              }}
            >
              <span className="project-action-button">
                <QesIcon iconName={QesIcons.ShareV6} description="Share Project" /> Share Project
              </span>
            </Link>
          </span>
          <ModalLink
            classNames="ml10 side-panel-link"
            disabled={isRunning || (isReadOnly && !state.project.resource.isArchived)}
            content={{
              body: `${archiveText} this project?`,
              cancelButtonText: "Cancel",
              confirmButtonAction: handleArchiveConfirm,
              confirmButtonText: archiveText,
              confirmButtonType: archiveText === "Archive" ? "primary-danger" : "primary",
              title: archiveText,
            }}
            testId="zip-project-controls__archive-project-modal-link"
          >
            <span className={`project-action-button ${isRunning || (isReadOnly && !state.project.resource.isArchived) ? " disabled" : ""}`}>
              <QesIcon iconName={QesIcons.Archive} description={`${archiveText} Project`} /> {archiveText} Project
            </span>
          </ModalLink>
          <span className="ml10">
            <Link
              to={{
                pathname: `/projects/${resourceId}/clone`,
                state: {
                  linkToProject: true,
                },
              }}
            >
              <span className="project-action-button">
                <QesIcon iconName={QesIcons.CopyV7} description="Clone Project" /> Clone Project
              </span>
            </Link>
          </span>
          <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.QUEST_ADMIN]}>
            <span className="ml15 project-action-button">
              <ZipJobsPanelContentLink projectId={resourceId} projectName={name} inline={true} />
            </span>
          </RoleRestricted>
        </>
      )
      }
    </>
  );
}